import type { TeamInput } from '@airmail/engine';
import { normalizeColonForm, parseTable } from './table.js';
import { splitPersonName, suggestMapping, titleCase, type ImportIssue } from './mapping.js';

/**
 * Liga-Modus (docs/13): Spielerliste je Mannschaft → feste Paare. Formen:
 *
 *   A) eine Zeile je Paar:     Ceva ; Spieler 1 ; Spieler 2
 *   B) eine Zeile je Spieler:  Ceva ; Spieler      (Reihenfolge bildet die Paare: 1+2, 3+4 …)
 *   C) mit eigenem Teamnamen:  Ceva 1 ; Spieler 1 ; Spieler 2         (Teamname wie geschrieben, Mannschaft „Ceva")
 *   D) vier Spalten:           Bag Brothers ; Spieler 1 ; Spieler 2 ; Ceva   (Team, Spieler, Mannschaft)
 *
 * Auch ohne Überschrift und mit Tab/Semikolon/Komma/Doppelpunkt. Die erste
 * Spalte ist die **Beschriftung**: Kommt sie mehrfach vor, ist sie die
 * Mannschaft und die Paare werden durchnummeriert („Ceva 1", „Ceva 2"). Kommt
 * sie nur einmal vor, ist sie der Teamname — die Mannschaft ist dann der Teil
 * vor der Nummer, wenn andere Zeilen denselben haben („Ceva 1", „Ceva 2" →
 * Mannschaft „Ceva"), sonst der Name selbst (ein Paar „Ceva" ist die Mannschaft
 * Ceva). Mit Überschriften „Team" und „Mannschaft" ist alles eindeutig.
 *
 * Harald, 09/2026: „Beim Import wird bei jedem Mannschaftsnamen eine 1
 * angehängt" — seine Liste hatte schon „Ceva 1 … 4" in der ersten Spalte,
 * daraus wurde „Ceva 1 1" mit Mannschaft „Ceva 1". Jetzt bleibt der Name, wie
 * er geschrieben ist.
 *
 * Ersatzspieler: Stern vor dem Namen („*Max Muster") oder „(Ersatz)" dahinter —
 * zählt für Paar und Mannschaft, nicht für die Spielerwertung.
 */
function person(raw: string): { firstName: string; lastName: string; substitute?: boolean } {
  const m = /^\s*\*\s*(.+)$|^(.+?)\s*\((?:ersatz|e|sub)\)\s*$/i.exec(raw);
  if (m) return { ...splitPersonName((m[1] ?? m[2] ?? '').trim()), substitute: true };
  return splitPersonName(raw);
}
export interface ClubImportResult {
  teams: TeamInput[];
  clubs: { name: string; players: number; pairs: number }[];
  issues: ImportIssue[];
}

/** Form „Mannschaft: Spieler; Spieler" — übernimmt der Tabellen-Parser (normalizeColonForm) */
export const normalizeClubText = normalizeColonForm;

/** „Ceva 1", „Mombarcaro1" → „Ceva", „Mombarcaro" — dieselbe Regel wie `team.clubsFromNames` in der Engine */
export function clubStem(name: string): string {
  return name.replace(/\s*\d+\s*$/, '').trim();
}

type Person = { firstName: string; lastName: string; substitute?: boolean };

export function importClubText(text: string, teamSize = 2): ClubImportResult {
  const table = parseTable(text);
  const issues: ImportIssue[] = [];
  // „Ceva" und „ceva" sind dieselbe Beschriftung — Schreibweise des ersten Vorkommens gewinnt
  const names = new Map<string, string>();
  const canon = (raw: string): string => {
    const k = raw.trim().toLowerCase().replace(/\s+/g, ' ');
    const known = names.get(k);
    if (known) return known;
    const nice = titleCase(raw.trim().replace(/\s+/g, ' '));
    names.set(k, nice);
    return nice;
  };
  if (table.rows.length === 0) return { teams: [], clubs: [], issues };

  const width = table.header.length;
  // Nur Überschriften sind eine verlässliche Aussage; aus Inhalten rät der Vorschlag nur
  const kopf = table.hasHeader ? suggestMapping(table) : null;
  let teamCol = kopf?.indexOf('teamName') ?? -1;
  let clubCol = kopf?.indexOf('club') ?? -1;
  // „Mannschaft" ist im Team-Import der Teamname, hier im Liga-Import die Mannschaft (früher „Gemeinde" —
  // die Überschrift gilt weiterhin, HEADER_RULES). Die Liga-Lesart gewinnt, sonst käme kein Paar zu seiner Mannschaft.
  const mannschaftCol = table.hasHeader ? table.header.findIndex((h) => /^mannschaft(en)?$/i.test(h.trim())) : -1;
  if (mannschaftCol >= 0 && clubCol < 0) {
    clubCol = mannschaftCol;
    if (teamCol === mannschaftCol) teamCol = -1;
  }
  const labelCol = teamCol >= 0 ? teamCol : clubCol >= 0 ? clubCol : 0;
  if (clubCol === labelCol) clubCol = -1;
  // Ohne Überschrift eine Spalte mehr als Beschriftung + Spieler: die letzte ist die Mannschaft (Form D)
  if (!table.hasHeader && clubCol < 0 && width === teamSize + 2) clubCol = width - 1;
  const playerCols = [...table.header.keys()].filter((i) => i !== labelCol && i !== clubCol);

  // Zeilen je Beschriftung sammeln, in Reihenfolge des ersten Auftretens
  const gruppen = new Map<string, { zeilen: number; spieler: Person[]; club?: string }>();
  let currentLabel = '';
  table.rows.forEach((row, ri) => {
    if (width === 1) {
      // einspaltig: Zeilen ohne Leerzeichen gelten als Mannschaft, sonst als Spieler
      const cell = (row[0] ?? '').trim();
      if (!cell) return;
      if (!/\s/.test(cell)) currentLabel = canon(cell);
      else if (currentLabel) {
        const g = gruppen.get(currentLabel) ?? { zeilen: 0, spieler: [] };
        g.zeilen++;
        g.spieler.push(person(cell));
        gruppen.set(currentLabel, g);
      } else issues.push({ row: ri, level: 'error', message: `Zeile ${ri + 1}: Mannschaft fehlt` });
      return;
    }
    const labelCell = (row[labelCol] ?? '').trim();
    const clubCell = clubCol >= 0 ? (row[clubCol] ?? '').trim() : '';
    const spieler = playerCols.map((i) => (row[i] ?? '').trim()).filter(Boolean);
    if (labelCell) currentLabel = canon(labelCell);
    if (spieler.length === 0) return; // Zeile nur mit Beschriftung
    if (!currentLabel) {
      issues.push({ row: ri, level: 'error', message: `Zeile ${ri + 1}: Mannschaft fehlt` });
      return;
    }
    const g = gruppen.get(currentLabel) ?? { zeilen: 0, spieler: [] };
    g.zeilen++;
    g.spieler.push(...spieler.map(person));
    if (clubCell && !g.club) g.club = canon(clubCell);
    gruppen.set(currentLabel, g);
  });

  // Stamm vor der Nummer, wenn ihn mehrere Beschriftungen teilen: „Ceva 1", „Ceva 2" → Mannschaft „Ceva"
  const stamm = new Map<string, number>();
  for (const label of gruppen.keys()) {
    const s = clubStem(label).toLowerCase();
    if (s) stamm.set(s, (stamm.get(s) ?? 0) + 1);
  }
  const gemeindeFuer = (label: string): string | undefined => {
    const s = clubStem(label);
    if (s && (stamm.get(s.toLowerCase()) ?? 0) >= 2) return canon(s);
    // Überschrift „Team" ohne Mannschafts-Spalte: nichts erfinden — auf der Teams-Seite nachtragen
    if (teamCol >= 0) return undefined;
    return label;
  };

  const teams: TeamInput[] = [];
  const summary = new Map<string, { name: string; players: number; pairs: number }>();
  let ohneGemeinde = 0;
  for (const [label, g] of gruppen) {
    const paare: Person[][] = [];
    for (let i = 0; i < g.spieler.length; i += teamSize) paare.push(g.spieler.slice(i, i + teamSize));
    const mehrere = paare.length > 1;
    // Mehrere Paare unter einer Beschriftung: die Beschriftung ist die Mannschaft
    const club = g.club ?? (mehrere ? label : gemeindeFuer(label));
    paare.forEach((chunk, i) => {
      const nr = i + 1;
      if (chunk.length < teamSize) issues.push({ row: -1, level: 'warning', message: `${club ?? label}: ${g.spieler.length} Spieler — Paar ${nr} ist unvollständig` });
      const team: TeamInput = { name: mehrere ? `${label} ${nr}` : label, players: chunk, paid: false };
      if (club) team.club = club;
      teams.push(team);
    });
    if (club) {
      const s = summary.get(club.toLowerCase()) ?? { name: club, players: 0, pairs: 0 };
      s.players += g.spieler.length;
      s.pairs += paare.length;
      summary.set(club.toLowerCase(), s);
    } else ohneGemeinde += paare.length;
  }
  const clubs = [...summary.values()];
  if (ohneGemeinde > 0) issues.push({ row: -1, level: 'warning', message: `${ohneGemeinde} Paare ohne Mannschaft — Spalte „Mannschaft" ergänzen oder später auf der Teams-Seite setzen` });
  if (clubs.length < 2 && ohneGemeinde === 0) issues.push({ row: -1, level: 'warning', message: 'Mindestens zwei Mannschaften nötig, sonst gibt es keine Spiele' });
  return { teams, clubs, issues };
}
